import { z } from 'zod';
import { EINVOICE_PROFILES, HANDOVERS, LEDGER_FRAMEWORKS, TAX_CODES, TAX_MODES } from './tax.js';
/**
 * Einstellungen der Finanzen (KON-139): Einrichtung → Finanzen. Alles hier ist vom Mandanten einstellbar, nichts ist fest verdrahtet.
 * Beraternummer und DATEV-Mandantennummer stehen in den Firmendaten (siehe `companyProfileInput`).
 */
const account = z.string().trim().regex(/^[0-9]{1,8}$/, 'Nur Ziffern, höchstens 8 Stellen');
const optionalAccount = account.nullable().or(z.literal('').transform(() => null));
export const financeSettingsInput = z
    .object({
    taxMode: z.enum(TAX_MODES),
    ledgerFramework: z.enum(LEDGER_FRAMEWORKS),
    accountLength: z.number('Bitte eine Zahl angeben').int().min(4, 'Mindestens 4 Stellen').max(8, 'Höchstens 8 Stellen'),
    fiscalYearStartMonth: z.number().int().min(1).max(12),
    handover: z.enum(HANDOVERS),
    paymentTermsDays: z.number('Bitte eine Zahl angeben').int().min(0, 'Nicht negativ').max(365, 'Höchstens 365 Tage'),
    /** Skonto in Basispunkten (2 % = 200), null = kein Skonto. Prozent und Tage gehören zusammen. */
    discountBp: z.number('Bitte eine Zahl angeben').int().min(1, 'Mehr als 0 %').max(10000, 'Höchstens 100 %').nullable(),
    discountDays: z.number('Bitte eine Zahl angeben').int().min(1, 'Mindestens 1 Tag').max(365, 'Höchstens 365 Tage').nullable(),
    footerText: z.string().trim().max(1000).nullable().transform((v) => (v ? v : null)),
    defaultBankAccountId: z.uuid('Ungültiges Konto').nullable(),
    einvoiceProfile: z.enum(EINVOICE_PROFILES),
    retentionYears: z.number('Bitte eine Zahl angeben').int().min(8, 'Das Gesetz verlangt mindestens 8 Jahre').max(30, 'Höchstens 30 Jahre'),
    /** Vier-Augen-Prinzip bei Eingangsrechnungen: Wer den Entwurf anlegt, gibt ihn nicht frei */
    purchaseFourEyes: z.boolean().default(false),
    /** Konten je Steuerschlüssel. Fehlende Schlüssel behalten ihre Konten, ein leerer Wert löscht sie. */
    taxAccounts: z
        .array(z.object({ code: z.string().refine((c) => TAX_CODES.some((t) => t.code === c), 'Unbekannter Steuerschlüssel'), revenueAccount: optionalAccount, taxAccount: optionalAccount }).strict())
        .max(TAX_CODES.length)
        .default([]),
})
    .strict()
    .superRefine((v, ctx) => {
    if ((v.discountBp === null) !== (v.discountDays === null))
        ctx.addIssue({ code: 'custom', path: ['discountDays'], message: 'Skonto braucht Prozent und Tage, oder beides leer' });
});
/** Vorgaben, solange der Mandant nichts eingestellt hat */
export const DEFAULT_FINANCE_SETTINGS = {
    ledgerFramework: 'none',
    accountLength: 4,
    fiscalYearStartMonth: 1,
    handover: 'file',
    paymentTermsDays: 14,
    discountBp: null,
    discountDays: null,
    footerText: null,
    defaultBankAccountId: null,
    einvoiceProfile: 'en16931',
    retentionYears: 8,
    purchaseFourEyes: false,
};
export const MONTH_LABELS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
// ---------------------------------------------------------------------------
// Bankkonten
// ---------------------------------------------------------------------------
/** IBAN ohne Leerzeichen in Großbuchstaben, mit Prüfziffer (Modulo 97) */
export function normalizeIban(value) {
    return value.replace(/\s+/g, '').toUpperCase();
}
export function isValidIban(value) {
    const iban = normalizeIban(value);
    if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban) || iban.length < 15 || iban.length > 34)
        return false;
    if (iban.startsWith('DE') && iban.length !== 22)
        return false;
    const rearranged = iban.slice(4) + iban.slice(0, 4);
    let remainder = 0;
    for (const char of rearranged) {
        const digits = /[A-Z]/.test(char) ? String(char.charCodeAt(0) - 55) : char;
        for (const d of digits)
            remainder = (remainder * 10 + Number(d)) % 97;
    }
    return remainder === 1;
}
const ibanField = z
    .string()
    .trim()
    .transform(normalizeIban)
    .refine((v) => /^[A-Z]{2}\d{2}/.test(v), 'Eine IBAN beginnt mit dem Länderkürzel und zwei Prüfziffern, zum Beispiel DE89')
    .refine(isValidIban, 'Diese IBAN ist nicht gültig. Bitte prüfen Sie Länge und Prüfziffer.');
const bicField = z
    .string()
    .trim()
    .transform((v) => v.replace(/\s+/g, '').toUpperCase())
    .refine((v) => /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(v), 'Eine BIC hat 8 oder 11 Zeichen, zum Beispiel COBADEFFXXX');
export const createBankAccountInput = z
    .object({
    name: z.string().trim().min(1, 'Bezeichnung fehlt').max(120),
    iban: ibanField,
    bic: z.union([bicField, z.literal('').transform(() => null), z.null()]).optional().transform((v) => v ?? null),
    isOwn: z.boolean().default(true),
})
    .strict();
export const updateBankAccountInput = z
    .object({ name: z.string().trim().min(1).max(120).optional(), bic: z.union([bicField, z.literal('').transform(() => null), z.null()]).optional(), isActive: z.boolean().optional() })
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
// ---------------------------------------------------------------------------
// Finanzdaten eines Kontakts (Debitor, Kreditor)
// ---------------------------------------------------------------------------
const vatId = z
    .string()
    .trim()
    .transform((v) => v.replace(/\s+/g, '').toUpperCase())
    .refine((v) => /^[A-Z]{2}[A-Z0-9]{2,13}$/.test(v), 'Ungültige USt-IdNr., zum Beispiel DE123456789')
    .refine((v) => !v.startsWith('DE') || /^DE\d{9}$/.test(v), 'Eine deutsche USt-IdNr. besteht aus DE und 9 Ziffern');
/** Ein leerer Text bedeutet „löschen“ und wird zu null, deshalb steht er vor dem eigentlichen Schema */
const blank = (schema) => z.union([z.literal('').transform(() => null), schema, z.null()]).optional();
export const updateContactFinanceInput = z
    .object({
    paymentTermsDays: z.number('Bitte eine Zahl angeben').int().min(0).max(365).nullable().optional(),
    discountBp: z.number('Bitte eine Zahl angeben').int().min(1).max(10000).nullable().optional(),
    discountDays: z.number('Bitte eine Zahl angeben').int().min(1).max(365).nullable().optional(),
    vatId: blank(vatId),
    taxCountry: blank(z.string().trim().toUpperCase().regex(/^[A-Z]{2}$/, 'Zwei Buchstaben, zum Beispiel DE')),
    reverseCharge: z.boolean().optional(),
    invoiceEmail: blank(z.string().trim().max(200).pipe(z.email('Ungültige E-Mail-Adresse'))),
    buyerReference: blank(z.string().trim().max(100)),
    iban: blank(ibanField),
    bic: blank(bicField),
    defaultTaxCode: blank(z.string().refine((c) => TAX_CODES.some((t) => t.code === c), 'Unbekannter Steuerschlüssel')),
    ledgerAccount: blank(account),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
export const assignNumberInput = z.object({ kind: z.enum(['debtor', 'creditor']) }).strict();
//# sourceMappingURL=settings.js.map